// ABOUTME: Client-side wrapper for navigation system
// ABOUTME: Uses NavContext for state and renders TopBar and IconSidebar
// ABOUTME: Shows minimal chrome on creator pages (/@username/*) to prioritize creator content

"use client"

import { useAuth } from "@clerk/nextjs"
import { usePathname } from "next/navigation"
import { useNavData } from "@/contexts/nav-context"
import { TopBar } from "./top-bar"
import { IconSidebar } from "./icon-sidebar"
import { CreatorTopBar } from "./creator-top-bar"

// Check if we're on a creator's public page (/@username or /@username/*)
function isCreatorPage(pathname: string): boolean {
  // Match /@username pattern (profile pages, posts, about, now, etc.)
  // But NOT paths like /@ (just the @ symbol) or paths that start with /api
  return pathname.startsWith("/@") && pathname.length > 2
}

// Check if we're on an editor page (no nav needed - editor has its own exit)
function isEditorPage(pathname: string): boolean {
  return pathname === "/new" || (pathname.includes("/posts/") && pathname.endsWith("/edit"))
}

// Public standalone pages that render their own nav (no NavWrapper needed)
function isPublicStandalonePage(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname === "/brand" ||
    pathname === "/privacy" ||
    pathname === "/tos" ||
    pathname === "/dmca" ||
    pathname === "/home-minimal" ||
    pathname === "/experimental-home" ||
    pathname === "/coming-soon" ||
    pathname === "/sign-in" ||
    pathname === "/sign-up" ||
    pathname.startsWith("/tools")
  )
}

export function NavWrapper() {
  const { isSignedIn } = useAuth()
  const pathname = usePathname()
  const { username, unreadNotificationsCount, loading } = useNavData()

  // Hide nav completely on editor pages, bookmarklet popup, and public standalone pages
  if (isEditorPage(pathname) || pathname === "/bookmarks/add" || isPublicStandalonePage(pathname)) {
    return null
  }

  if (loading) {
    return null
  }

  // On creator pages for logged-out visitors, show minimal chrome
  // This puts the focus on the creator's content, not Drafty branding
  if (isCreatorPage(pathname) && !isSignedIn) {
    return (
      <CreatorTopBar
        isAuthenticated={false}
        username={username ?? undefined}
        unreadNotificationsCount={unreadNotificationsCount}
      />
    )
  }

  // For logged-in users (anywhere) or non-creator pages, show full navigation
  // On creator pages, only show mobile TopBar (no desktop sidebar)
  const onCreatorPage = isCreatorPage(pathname)

  return (
    <>
      {/* TopBar: mobile-only for dashboard, always visible on creator pages */}
      <TopBar
        isAuthenticated={!!isSignedIn}
        username={username ?? undefined}
        unreadNotificationsCount={unreadNotificationsCount}
        showOnDesktop={onCreatorPage}
      />
      {/* Desktop: Icon sidebar - hide on creator pages to keep focus on content */}
      {isSignedIn && !onCreatorPage && (
        <IconSidebar
          username={username ?? undefined}
          unreadNotificationsCount={unreadNotificationsCount}
        />
      )}
    </>
  )
}
